import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';

import css from './DescriptionSection.module.css';

/**
 * Number of lines shown before the "Les mer" toggle clamps the rest.
 * Mirrors the value used in the CSS module (`-webkit-line-clamp: 3`).
 */
const COLLAPSED_LINE_LIMIT = 3;

/**
 * "Beskrivelse" / Description section on the ListingPage right column.
 * Renders the listing description with a "Les mer" toggle when the text
 * exceeds the collapsed line limit (CSS line-clamp). Toggle disappears
 * automatically for short descriptions.
 *
 * @param {Object} props
 * @param {string} props.text – the listing description (plain text)
 * @returns {JSX.Element|null}
 */
const DescriptionSection = props => {
  const { text } = props;
  const intl = useIntl();

  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  // Detect overflow once after layout so we know whether the toggle is
  // needed at all. Uses scrollHeight vs clientHeight comparison while the
  // text is in its collapsed state.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setIsClamped(el.scrollHeight > el.clientHeight + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  if (!text) return null;

  const showToggle = isClamped || expanded;

  return (
    <section className={css.root}>
      <h4 className={css.heading}>
        {intl.formatMessage({ id: 'ListingPage.descriptionHeading' })}
      </h4>
      <p
        ref={textRef}
        className={classNames(css.text, { [css.textCollapsed]: !expanded })}
      >
        {text}
      </p>
      {showToggle ? (
        <button
          type="button"
          className={css.toggle}
          onClick={() => setExpanded(prev => !prev)}
        >
          {intl.formatMessage({
            id: expanded ? 'ListingPage.descriptionReadLess' : 'ListingPage.descriptionReadMore',
          })}
        </button>
      ) : null}
    </section>
  );
};

export default DescriptionSection;
